import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { IAPIFetchRequestOptions, IAPIFetchResponse } from '@sondermind/utilities/models-http';

import {
  IPersonaProviderProfileResponse,
  PersonaProviderProfile
} from '../models/persona-provider-profile';

@Injectable({
  providedIn: 'root'
})

export class PersonaProvidersHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) {}

  attestTelehealth(opts: Partial<IAPIFetchRequestOptions> = {}): Observable<void> {
    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/attest_telehealth`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .post<unknown>(url, {}, params)
      .pipe(
        map((_) => undefined),
        this.http.handleHttpError(opts.error)
      );
  }

  /*
     * get current user's persona provider profile
     */
  getPersonaProviderProfile(opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/me`;
    const params = this.http.generateFetchParams(opts);

    return this.http
      .get<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  getPersonaProviderProfileById(personaProviderId: string, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId}`;

    return this.http
      .get<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  updateDirectSchedulePref(enabledStatus: boolean, providerId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<null> {
    const params = this.http.generateFetchParams(opts);
    const status = enabledStatus ? 'enable' : 'disable';
    return this.http
      .post<null>(`${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${providerId.toString()}/direct_scheduling/${status}`, params)
      .pipe(
        this.http.handleHttpError()
      );
  }

  /*
     * update the current user's acceptingSamClients status
     * success response returns full profile
     */
  updateAcceptingSamClients(acceptingSamClients: boolean, personaProviderId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
        accepting_sam_clients: acceptingSamClients
      }
    };

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId.toString()}`;

    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  updateSessionTypesOffered(
    sessionTypesOffered: string[],
    personaProviderId: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
        session_types_offered: sessionTypesOffered
      }
    };

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId.toString()}`;
    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  /*
     * update the current user's acceptingClients status
     * success response returns full profile
     */
  updateGeneralAvailability(acceptingClients: boolean, personaProviderId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
        accepting_clients: acceptingClients
      }
    };

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId.toString()}`;
    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  /*
     * update the current user's allows_consultation status
     * success response returns full profile
     */
  updateAllowsConsultation(allowsConsultation: boolean, personaProviderId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
        allows_consultation: allowsConsultation
      }
    };

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId.toString()}`;
    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  /**
     * Update the current user's lgbtIdentified status
     * success response returns full profile
     *
     * @returns {*} {Observable<PersonaProviderProfile>}
     * @memberof PersonaProvidersHttpService
    */
  updateLgbtIdentified$(lgbtIdentified: string, personaProviderId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
        lgbt_identified: lgbtIdentified
      }
    };

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId.toString()}`;
    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  updateTelehealthRecordingConsent(value: number, personaProviderId: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<PersonaProviderProfile> {
    const params = this.http.generateFetchParams(opts);
    const updateParams: IPersonaProviderUpdateParams = {
      persona_provider: {
      }
    };
    if (value === 1) {
      updateParams.persona_provider.transcription_consent_at = new Date().toISOString();
    } else if (value === 2) {
      updateParams.persona_provider.transcription_revoked_consent_at = new Date().toISOString();
    }

    const url = `${this.configurationService.env.svcBase}/provider_profiles/persona_providers/${personaProviderId}`;
    return this.http
      .put<IAPIFetchResponse<IPersonaProviderProfileResponse>>(url, updateParams, params)
      .pipe(
        map((resp: IAPIFetchResponse<IPersonaProviderProfileResponse>) => new PersonaProviderProfile(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }
}

interface IPersonaProviderUpdateParams {
  persona_provider: {
    accepting_clients?: boolean;
    accepting_sam_clients?: boolean;
    allows_consultation?: boolean;
    session_types_offered?: string[];
    lgbt_identified?: string;
    transcription_consent_at?: string | null;
    transcription_revoked_consent_at?: string | null;
  };
}
